export const ROLE_ADMIN = 'admin';
export const ROLE_DISPATCHER = 'dispatcher';
export const ROLE_DRIVER = 'driver';
export const ROLE_DEALER = 'dealer';
export const ROLE_DEALER_ADMIN = 'dealer-admin';
export const ROLE_DEALER_SUPER_ADMIN = 'dealer-super-admin';

/** All dealer-tier roles (for GQL _in filters) */
export const DEALER_ROLES = [ROLE_DEALER, ROLE_DEALER_ADMIN, ROLE_DEALER_SUPER_ADMIN] as const;

/** Portal roles — users with these roles can access the dealer portal */
const PORTAL_ROLES = new Set<string>([ROLE_DEALER, ROLE_DEALER_ADMIN, ROLE_DEALER_SUPER_ADMIN]);

/** Returns true for roles that grant portal access (dealer, dealer-admin, dealer-super-admin) */
export function isPortalRole(role: string | undefined | null): boolean {
  return !!role && PORTAL_ROLES.has(role);
}

/** Returns true for the HopDrive system admin role */
export function isAdminUser(role: string | undefined | null): boolean {
  return role === ROLE_ADMIN;
}

/** Returns true if the user's role is driver and nothing else */
export function isDriverOnly(role: string | undefined | null): boolean {
  return role === ROLE_DRIVER;
}

/** Returns true for any dealer-tier role (dealer, dealer-admin, dealer-super-admin) */
export function isDealerRole(role: string | undefined | null): boolean {
  return !!role && PORTAL_ROLES.has(role);
}

/** Role options for dropdowns and toggle groups */
export const ROLE_OPTIONS = [
  { value: ROLE_DRIVER, label: 'Driver' },
  { value: ROLE_DEALER, label: 'Dealer' },
  { value: ROLE_DEALER_ADMIN, label: 'Dealer Admin' },
  { value: ROLE_DEALER_SUPER_ADMIN, label: 'Super Admin' },
] as const;

/** Mapping of role keys to display labels */
export const ROLE_LABELS: Record<string, string> = {
  [ROLE_ADMIN]: 'Admin',
  [ROLE_DISPATCHER]: 'Dispatcher',
  [ROLE_DRIVER]: 'Driver',
  [ROLE_DEALER]: 'Dealer',
  [ROLE_DEALER_ADMIN]: 'Dealer Admin',
  [ROLE_DEALER_SUPER_ADMIN]: 'Super Admin',
};

/** Get the human-readable label for a role key. */
export function getRoleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

/** Per-role chip color config: bg + text color */
export interface RoleChipStyle { bg: string; color: string }

export const ROLE_CHIP_STYLES: Record<string, RoleChipStyle> = {
  [ROLE_ADMIN]:              { bg: '#fff5f3', color: '#ff4830' },  // primaryAccent
  [ROLE_DEALER_SUPER_ADMIN]: { bg: '#fff5f3', color: '#ff4830' },  // primaryAccent
  [ROLE_DEALER_ADMIN]:       { bg: '#eef4fb', color: '#4068a0' },  // secondaryAccent
  [ROLE_DEALER]:             { bg: '#eef4fb', color: '#4068a0' },  // secondaryAccent
  [ROLE_DRIVER]:             { bg: '#fff9f3', color: '#a6681f' },  // warning
};

const DEFAULT_CHIP_STYLE: RoleChipStyle = { bg: '#f4f4f5', color: '#60666d' };

export function getRoleChipStyle(role: string): RoleChipStyle {
  return ROLE_CHIP_STYLES[role] ?? DEFAULT_CHIP_STYLE;
}

/** Short tooltip descriptions for each role */
export const ROLE_TOOLTIPS: Record<string, string> = {
  [ROLE_DRIVER]: 'Access to mobile app only',
  [ROLE_DEALER]: 'Access to a single rooftop',
  [ROLE_DEALER_ADMIN]: 'Access to multiple rooftops',
  [ROLE_DEALER_SUPER_ADMIN]: 'Access to all rooftops',
};

export interface RoleUser {
  id: number;
  default_role: string;
  active: boolean;
}

/**
 * Determine if a user's role should change based on their new customer count.
 * - dealer -> dealer-admin when linked to >=2 customers
 * - dealer-admin -> dealer when linked to <=1 customer
 * Returns the target role string, or null if no change is needed.
 */
export function calculateTargetRole(
  currentRole: string,
  newCustomerCount: number,
): string | null {
  if (newCustomerCount >= 2 && currentRole === ROLE_DEALER) {
    return ROLE_DEALER_ADMIN;
  }
  if (newCustomerCount <= 1 && currentRole === ROLE_DEALER_ADMIN) {
    return ROLE_DEALER;
  }
  return null;
}

/**
 * Check whether a user has access to a specific customer.
 */
export function userHasCustomerAccess(
  userCustomerIds: number[],
  customerId: number,
): boolean {
  return userCustomerIds.includes(customerId);
}

/**
 * Check if a role is dealer-super-admin.
 */
export function isSuperAdmin(role: string): boolean {
  return role === ROLE_DEALER_SUPER_ADMIN;
}

/**
 * Authorization: may `currentUserRole` modify a target user's access
 * (rooftop access or feature access)?
 *
 * A super admin may only be modified by another super admin. This prevents a
 * dealer / dealer-admin from stripping or altering a super admin's access. All
 * non-super-admin targets remain manageable by any portal manager (existing
 * behavior). Mirrors the super-admin-only convention already used for role
 * promotion (see UserProfileTab `canPromoteToSuperAdmin`).
 *
 * UI guard only — the authoritative boundary is the backend (Hasura
 * permissions). Defaults to "deny" for super-admin targets when the current
 * role is unknown (e.g. still loading), the safe direction.
 */
export function canManageUserAccess(
  currentUserRole: string | null | undefined,
  targetUserRole: string,
): boolean {
  if (!isSuperAdmin(targetUserRole)) return true;
  return isSuperAdmin(currentUserRole ?? '');
}

/**
 * Check if a user has the driver role granted in their usertoroles.
 * Accepts the users_usertoroles array from any user query that includes it.
 */
export function hasDriverRole(usertoroles: Array<{ roletype?: { name?: string } | null }> | undefined | null): boolean {
  if (!usertoroles) return false;
  return usertoroles.some(r => r.roletype?.name === 'driver');
}

/**
 * Count active super admins in a list of users.
 */
export function countActiveSuperAdmins(users: RoleUser[]): number {
  return users.filter(u => u.active && isSuperAdmin(u.default_role)).length;
}

/**
 * Returns true only if demoting the given user still leaves >=1 other active super admin.
 */
export function canDemoteSuperAdmin(users: RoleUser[], userId: number): boolean {
  const otherActiveSuperAdmins = users.filter(
    u => u.id !== userId && u.active && isSuperAdmin(u.default_role),
  );
  return otherActiveSuperAdmins.length >= 1;
}

/**
 * Returns true unless the user is the sole active super admin.
 */
export function canDeactivateUser(users: RoleUser[], userId: number): boolean {
  const user = users.find(u => u.id === userId);
  if (!user || !isSuperAdmin(user.default_role)) return true;
  return canDemoteSuperAdmin(users, userId);
}

/**
 * Returns true when zero active super admins exist in the list.
 */
export function shouldDefaultToSuperAdmin(users: RoleUser[]): boolean {
  return countActiveSuperAdmins(users) === 0;
}
